/**
 * Fulfillment-job widgets (#1805 slice A4b).
 *
 * The six `fields[].render` callbacks the dispatch job list carried, plus the
 * `copyable` affordance on `shopify_fulfillment_id`, moved to their only v2
 * carriers.
 *
 * `resolveJobOrderName` / `formatJobOrderLabel` live HERE rather than in
 * `./jobResource.tsx`, and the direction of that import is the point.
 * `src/features/widgets.ts` is evaluated by `src/test-setup.ts` for EVERY
 * suite, ahead of any `vi.mock`, so a service reached from a widget module is
 * instantiated against the real `@lib/api` before a test can replace it (that
 * cost 19 red tests in slice A1) — and `./jobResource.tsx` imports `http` at
 * module scope. Two cells need the label and so does the detail title, so
 * exactly one of the two files must own it, and it has to be this one. Both are
 * re-exported from `./jobResource.tsx` so their public import path does not
 * move.
 *
 * The last registration is the trap of this slice. `shopify_fulfillment_id` is
 * `type: 'text'` + `copyable: true` (#1291), NOT an `id` column, and `copyable`
 * renders through the id widget while EXPORTING through the field's own type.
 * Re-typing the column `id` would therefore keep the markup byte-identical
 * while moving its CSV from `'' → -` to `'' → ''`
 * (`lib/__tests__/csv-export-baseline.json` records the `-` form as the frozen
 * text). `jobCopyableId` borrows the id widget's DISPLAY and declares NO
 * `csvText`, which leaves the text projection exactly where it was.
 *
 * The other six declare `csvText: stringifyRaw(value)`, the text the
 * bespoke-renderer branch of `formatFieldTextForExport` already produced. They
 * translate through the i18n singleton because the callbacks they replace did:
 * only the literal copy guarantees the byte-identical markup
 * `__tests__/jobResource.v2-parity.test.tsx` axis ① compares.
 */
import i18n from '@/i18n';
import { failHard } from '@resource-kit/core';
import {
  FIELD_TYPE_WIDGETS,
  stringifyRaw,
  Badge,
  EntityLink,
  ExternalServiceLink,
  CopyButton,
  IdCell,
} from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { FieldType, Widget, WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import { DisplayTable, type DisplayColumn } from '@components/ui/DisplayTable';
import { RawDataDisclosure } from '@components/ui/RawDataDisclosure';
import { AddressLink } from '@components/ui/AddressLink';
import {
  isLineItemRecord,
  lineItemProductPath,
  lineItemTitle,
  parseLineItemsJson,
  type LineItem,
} from '@lib/line-items';
import { EXTERNAL_URLS } from '@/constants/config';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import type { FulfillmentJob } from '@/services';
import { JobStatusRoute } from './components/JobStatusRoute';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    jobName: { types: 'text'; modes: 'list' };
    jobOrder: { types: 'text'; modes: 'list' };
    jobOnfleetTaskId: { types: 'text'; modes: 'list' };
    jobOnfleetTaskShortId: { types: 'text'; modes: 'list' };
    jobWrappedNote: { types: 'text'; modes: 'list' };
    jobCopyableId: { types: 'text'; modes: 'list' };
    jobLineItems: { types: 'json'; modes: 'list' };
    jobStatusRoute: { types: 'enum'; modes: 'list' };
    jobAddress: { types: 'text'; modes: 'list' };
    jobCustomerPhone: { types: 'text'; modes: 'list' };
  }
}

/** The field type's own widget — level 3 of the resolution order. */
function typeWidget(type: FieldType): Widget {
  return (
    FIELD_TYPE_WIDGETS.get(type) ??
    failHard(`no default widget is registered for field type "${type}"`)
  );
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The job behind the cell. A widening cast, not a `failHard`: the callbacks
 * these replace read optional members off whatever they were handed.
 */
function jobOf(record: unknown): Partial<FulfillmentJob> {
  return typeof record === 'object' && record !== null ? (record as Partial<FulfillmentJob>) : {};
}

export function resolveJobOrderName(row: Partial<FulfillmentJob>): string | null {
  return row.shopify_order_name?.trim() || null;
}

/**
 * The `#order · date` label. Core-written jobs carry a null `name` column but a
 * populated `shopify_order_name`, so this is what keeps the list from showing
 * 未命名 / Unnamed for a job that has a perfectly good order behind it (#879).
 */
export function formatJobOrderLabel(row: Partial<FulfillmentJob>): string | null {
  const orderName = resolveJobOrderName(row);
  if (!orderName) return null;
  if (!row.delivery_date) return orderName;

  return i18n.t('job.orderLabel', {
    name: orderName,
    date: row.delivery_date,
  });
}

/**
 * The delivery dates whose line items answered the search — but only when this
 * row's OWN date is not among them (#2128).
 *
 * The search reaches the items through the order, and the order's blob holds
 * every delivery date's items (#2069's accepted imprecision, kept by the owner
 * ruling on #2082). So an operator searching 「Lavender Lemonade」 also gets
 * the same order's delivery job for the day after, with nothing on screen
 * saying why. Null here means "nothing surprising happened" — the row's own
 * goods matched, or the hit came from the order number / operator note, or
 * this was not a search at all — and the cell renders exactly as it always did.
 * Labelling every row would be noise an operator learns to stop reading.
 */
function foreignSearchHitDates(row: Partial<FulfillmentJob>): string[] | null {
  const dates = row.search_hit_dates;
  if (!Array.isArray(dates) || dates.length === 0) return null;
  const own = row.delivery_date;
  return typeof own === 'string' && dates.includes(own) ? null : dates;
}

function JobName({ value, record, surface }: WidgetProps) {
  // Core-written jobs carry a null `name` column but a populated
  // `shopify_order_name`; derive the order label (`#order · date`)
  // via the same priority chain as the order_id column and the
  // dispatch-preview label, so the list 名称 column shows a real
  // label instead of 未命名 / Unnamed (#879).
  const label = (value as string | null) ?? formatJobOrderLabel(jobOf(record));
  if (!label) return <span className="text-muted-foreground">{i18n.t('job.unnamed')}</span>;
  const text = <span className="font-mono text-xs">{label}</span>;

  /*
   * LIST only (#1969). This is the way into the record: every other identity
   * column beside it links somewhere — `order_id` to the order, both Onfleet
   * ids to Onfleet — and the one naming THIS job was the only dead text among
   * them, with no hover cue and no middle-click for an operator working two
   * jobs side by side. A row click already navigates here; the link adds the
   * affordances a row click cannot have.
   *
   * On the DETAIL surface the same widget renders the title of the record
   * already on screen, and a link to self is a click that changes nothing.
   */
  const job = jobOf(record);
  const id = job.id;
  if (surface === 'detail' || !id) return text;
  const link = <EntityLink to={`/dispatch/jobs/${id}`}>{text}</EntityLink>;

  // LIST only, and only on the exception. The record page is not a list of
  // search results, and a row whose own items matched gets the markup it had.
  const foreign = foreignSearchHitDates(job);
  if (foreign === null) return link;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {link}
      <Badge variant="outline" title={i18n.t('job.searchHit.otherDateHint')}>
        {i18n.t('job.searchHit.otherDate', { dates: foreign.join(' / ') })}
      </Badge>
    </span>
  );
}

/** One item's product name, linked into this admin's product record. */
function JobLineItemTitle({ item }: { item: LineItem }) {
  const title = lineItemTitle(item);
  const href = lineItemProductPath(item);
  // A line naming no product has nothing to link to — a handwritten line, a
  // legacy row. A link to `/products/null` is worse than no link.
  if (title === null) return <span className="text-muted-foreground">—</span>;
  return href === null ? (
    <span className="text-foreground">{title}</span>
  ) : (
    <EntityLink to={href}>{title}</EntityLink>
  );
}

/** A job's line, as the raw-data view marks it (ADR-0046 decision 6). */
type JobLineItem = LineItem & { _job_quantity?: number };

function hasJobQuantity(item: JobLineItem): boolean {
  return typeof item._job_quantity === 'number';
}

const JOB_LINE_ITEM_COLUMNS: DisplayColumn<JobLineItem>[] = [
  {
    key: 'title',
    header: i18n.t('lineItem.title'),
    cell: (item) => <JobLineItemTitle item={item} />,
  },
  {
    key: 'quantity',
    header: i18n.t('lineItem.quantity'),
    align: 'right',
    cell: (item) => item.quantity ?? 0,
  },
];

/**
 * A line this job's 配送单清单 does not hold, shown muted. Each cell paints its
 * own colour (the product link's `text-accent`, a plain title's
 * `text-foreground`), so the row colour alone would lose to them: `**:` makes
 * every descendant inherit it. A row without `_job_quantity` gets no class.
 */
function muteUnheldRow(item: JobLineItem): string | undefined {
  return item._job_quantity === 0 ? 'text-muted-foreground **:text-inherit' : undefined;
}

const JOB_LINE_ITEM_COLUMNS_WITH_JOB_QUANTITY: DisplayColumn<JobLineItem>[] = [
  ...JOB_LINE_ITEM_COLUMNS,
  {
    key: 'jobQuantity',
    header: i18n.t('lineItem.jobQuantity'),
    align: 'right',
    cell: (item) => item._job_quantity ?? 0,
  },
];

/**
 * The job's items, as a table (#1969).
 *
 * `line_items_json` is a wire STRING of verbatim Shopify line items, and the
 * `json` field type rendered it as the raw key→value tree — an operator
 * checking what goes in the box had to read `properties[0].value` out of a
 * nested blob. It holds the same elements the order detail table holds, so it
 * renders the same way, and the blob stays one disclosure away for whoever
 * needs `variant_id` or `requires_shipping`.
 *
 * Never the order's four columns (owner call, 2026-08-05): the job wire
 * carries no `currency`, so a money column here could only be an unlabelled
 * number. The prices are on the order, one link away. A job whose 配送单清单
 * is NULL renders two columns; a job with one adds a third (below).
 *
 * The SCOPE of this list (ADR-0046 decision 6): for a job with a 配送单清单
 * the backend lists every order line and marks each with `_job_quantity`,
 * this job's 配送单清单 quantity, so the table adds a 本配送单数量 column and
 * mutes a row whose quantity is 0. A job whose 配送单清单 is NULL (an old job)
 * still carries only the lines dated for its delivery date, with no mark, and
 * renders the two columns as before; the label is only an approximation for
 * those jobs, a known trade-off.
 */
function JobLineItems(props: WidgetProps) {
  const { value, surface } = props;
  /*
   * LIST keeps the json type's own compact projection ("N items"). The
   * column is `columnInvisible`, but the operator can switch it back ON from
   * the column picker — and a full table in every row of a list is not a
   * denser view of the same thing, it is an unreadable one. Delegating to
   * the type widget rather than re-deriving a count keeps that projection
   * byte-identical to what every other json column renders.
   */
  if (surface !== 'detail') {
    const Display = typeWidget('json').display;
    return <Display {...props} />;
  }
  const entries = parseLineItemsJson(value);
  const empty = <span className="text-sm text-muted-foreground">{i18n.t('lineItem.empty')}</span>;
  if (entries.length === 0) return empty;
  // A non-object entry is drawn as no row; the raw disclosure keeps it.
  const items: JobLineItem[] = entries.filter(isLineItemRecord);
  if (items.length === 0) {
    return (
      <div>
        {empty}
        <RawDataDisclosure value={entries} />
      </div>
    );
  }
  const marked = items.some(hasJobQuantity);
  return (
    <div>
      <DisplayTable
        columns={marked ? JOB_LINE_ITEM_COLUMNS_WITH_JOB_QUANTITY : JOB_LINE_ITEM_COLUMNS}
        rows={items}
        ariaLabel={i18n.t('lineItem.title')}
        rowClassName={muteUnheldRow}
      />
      <RawDataDisclosure value={entries} />
    </div>
  );
}

function JobOrder({ value, record }: WidgetProps) {
  const orderId = value as string;
  const orderLabel = formatJobOrderLabel(jobOf(record));
  return (
    <div className="flex flex-col">
      {orderLabel ? (
        <span className="inline-flex items-center gap-1">
          <EntityLink to={`/orders/${orderId}`} className="font-mono text-xs">
            {orderLabel}
          </EntityLink>
          <CopyButton value={orderLabel} />
        </span>
      ) : (
        <span
          className="inline-flex flex-col items-start gap-0.5 text-muted-foreground"
          title={i18n.t('job.unresolvedOrderTooltip')}
        >
          <IdCell value={orderId} truncate={12} linkTo={`/orders/${orderId}`} />
          <span className="text-[10px] font-medium uppercase text-destructive">
            {i18n.t('job.unresolvedOrder')}
          </span>
        </span>
      )}
    </div>
  );
}

function JobOnfleetTaskId({ value }: WidgetProps) {
  const id = value as string | null;
  if (!id) return <span className="text-muted-foreground">-</span>;
  // Detail view only (`columnInvisible`). Show the full id with
  // copy + an external "open in Onfleet" affordance — the
  // operator commonly cross-references this id in the Onfleet
  // dashboard, so visible truncation would be a regression.
  return (
    <span className="inline-flex items-center gap-2">
      <IdCell value={id} truncate={false} />
      <ExternalServiceLink
        href={EXTERNAL_URLS.onfleetTask(id)}
        ariaLabel={i18n.t('ui.openInOnfleet')}
      />
    </span>
  );
}

function JobOnfleetTaskShortId({ value, record }: WidgetProps) {
  const shortId = value as string | null;
  if (!shortId) return <span className="text-muted-foreground">-</span>;
  const fullId = jobOf(record).onfleet_task_id;
  // When the full id is missing (data anomaly — should not
  // happen for rows written by current dispatch, but guard for
  // legacy rows or partial writes), fall back to a plain mono
  // label rather than a broken link.
  if (!fullId) return <span className="font-mono text-xs">{shortId}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <ExternalServiceLink
        href={EXTERNAL_URLS.onfleetTask(fullId)}
        label={shortId}
        // The visible `shortId` is the operator's *handle* but
        // doesn't tell a screen reader what activating the link
        // does. The explicit ariaLabel makes it unambiguous.
        ariaLabel={i18n.t('ui.openInOnfleet')}
      />
      <CopyButton value={shortId} />
    </span>
  );
}

/**
 * Free-text operator notes: the only cells on this resource that must keep
 * their author's line breaks, hence `whitespace-pre-wrap`. One widget serves
 * both `task_note` and `operator_note` — the two callbacks it replaces were
 * byte-identical.
 */
function JobWrappedNote({ value }: WidgetProps) {
  const note = value as string | null | undefined;
  if (!note) return <span className="text-muted-foreground">-</span>;
  return <span className="whitespace-pre-wrap break-words">{note}</span>;
}

/**
 * The status column as the mini route (#2588). The label comes from
 * `field.options`, not `jobStatusOptions`: this module must not import
 * `./jobResource.tsx` (see the file header). The DETAIL surface delegates to
 * the enum type's own Badge unchanged, and so does an empty value.
 */
function JobStatusRouteCell(props: WidgetProps) {
  const { value, field, surface } = props;
  if (surface === 'detail' || value === null || value === undefined || value === '') {
    const Display = typeWidget('enum').display;
    return <Display {...props} />;
  }
  const status = String(value);
  return (
    <JobStatusRoute
      status={status}
      label={lookupEnumLabelOrWarn(status, field.options ?? [], 'job status')}
    />
  );
}

/**
 * Both job addresses, `delivery_address` and `original_address` (#2677,
 * widened by #2689): a non-empty value is a Google Maps search link with a
 * copy button beside it on EVERY surface. An empty value is the text type's
 * own display unchanged.
 */
function JobAddress(props: WidgetProps) {
  const { value } = props;
  if (typeof value !== 'string' || value === '') {
    const Display = typeWidget('text').display;
    return <Display {...props} />;
  }
  return <AddressLink address={value} />;
}

/**
 * The customer phone (#2680): a non-empty value is a `tel:` link on EVERY
 * surface, so an operator on a phone can call from the job card. The link
 * stops its click: the card title row and the table row both open the job.
 * An empty value is the text type's own display unchanged.
 *
 * Not the `phone` field type: its display is shared with drivers and
 * suppliers, and the field keeps the `text` type's filter and CSV projection.
 */
function JobCustomerPhone(props: WidgetProps) {
  const { value } = props;
  if (typeof value !== 'string' || value === '') {
    const Display = typeWidget('text').display;
    return <Display {...props} />;
  }
  return (
    <a
      href={`tel:${value}`}
      onClick={(e) => e.stopPropagation()}
      className="text-sm text-primary underline-offset-2 hover:underline"
    >
      {value}
    </a>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerJobWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('jobName', {
    display: JobName,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('jobOrder', {
    display: JobOrder,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('jobOnfleetTaskId', {
    display: JobOnfleetTaskId,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('jobOnfleetTaskShortId', {
    display: JobOnfleetTaskShortId,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('jobWrappedNote', {
    display: JobWrappedNote,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('jobLineItems', {
    display: JobLineItems,
    csvText: rawCsv,
    supportedTypes: ['json'],
    modes: ['list'],
  });
  registerWidget('jobCopyableId', {
    // The copy affordance, borrowed from the id type's DISPLAY only — see
    // the file header for why `csvText` is deliberately absent here.
    display: typeWidget('id').display,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  // No `csvText`: the CSV keeps the enum type's own projection, as
  // `jobCopyableId` does.
  registerWidget('jobStatusRoute', {
    display: JobStatusRouteCell,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  // No `csvText`: the CSV keeps the text type's own projection
  // (`lib/__tests__/csv-export-baseline.json`), as `jobCopyableId` does.
  registerWidget('jobAddress', {
    display: JobAddress,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  // No `csvText`: the CSV keeps the text type's own projection, as
  // `jobAddress` does.
  registerWidget('jobCustomerPhone', {
    display: JobCustomerPhone,
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
